import { useMemo, useState } from 'react';
import { hireMatchesSearch } from '../utils/hireSearch';

/**
 * The hire-search filter derivation, shared by `pages/MyHires.js` and
 * `pages/AdminHires.js` (2026-09-13).
 *
 * 🔑 **EXTRACTED RATHER THAN COPIED TWICE.** `pages/DepartmentHires.js` carries
 * this exact derivation (`filteredItems` / `filteredEvents` /
 * `searchHidEverything`) inline as three `useMemo`s — see that file's own
 * docblocks for the full reasoning behind each rule below, which is not
 * repeated here. Wiring the same three `useMemo`s into a SECOND page
 * (`MyHires.js`) would already be a near-copy of `DepartmentHires.js`; wiring
 * them into a THIRD (`AdminHires.js`) as well would make it a three-way
 * duplicate `check-duplication` was always going to flag. This hook is the
 * shared derivation for the two pages that may take it.
 *
 * 🚨 **`DepartmentHires.js` is DELIBERATELY NOT migrated onto this hook by
 * this change** — the file is off-limits to this task (another agent's
 * exclusive write today). Migrating it is OWED WORK: once done, all three
 * pages import one hook and the remaining duplication score against its
 * inline `useMemo`s goes to zero. Until then, `check-duplication` may still
 * see a two-way similarity between this hook's function bodies and
 * `DepartmentHires.js`'s inline ones — that is the cost of extracting for two
 * callers while a third, un-editable file keeps the old shape, not a defect
 * in the extraction itself.
 *
 * Returns `{ searchTerm, setSearchTerm, filteredItems, filteredEvents,
 * searchHidEverything }`. `items` and `calendarEvents` are the page's own
 * fetched arrays; this hook filters them in memory and issues no request —
 * declared `CLIENT_FILTER` in `hooks/useSearchRequest.staleGuardManifest.test.js`
 * for both callers, the same declaration `DepartmentHires.js` carries.
 */
export default function useHireSearch(items, calendarEvents) {
  const [searchTerm, setSearchTerm] = useState('');

  /** What every VIEW renders. `utils/hireSearch.js` owns the predicate. */
  const filteredItems = useMemo(
    () => items.filter(item => hireMatchesSearch(item, searchTerm)),
    [items, searchTerm]
  );

  /**
   * The calendar's events, narrowed to the hires the search kept, by
   * MEMBERSHIP (`item_id`), never by re-running the predicate over an event —
   * `GET /api/hires/calendar` selects a strict subset of `GET /api/hires`'s
   * columns, so the two would answer differently for the same hire. An empty
   * term returns `calendarEvents` UNTOUCHED rather than the membership filter
   * — see `DepartmentHires.js`'s matching comment for why.
   */
  const filteredEvents = useMemo(() => {
    if (!searchTerm.trim()) return calendarEvents;
    const keptIds = new Set(filteredItems.map(i => i.item_id));
    return calendarEvents.filter(ev => keptIds.has(ev.item_id));
  }, [calendarEvents, filteredItems, searchTerm]);

  /**
   * TRUE-empty vs SEARCH-empty. An empty list is a claim the user acts on —
   * telling someone they have no hires when their term simply matched none is
   * a false claim. See `DepartmentHires.js`'s matching comment.
   */
  const searchHidEverything = items.length > 0 && filteredItems.length === 0;

  return { searchTerm, setSearchTerm, filteredItems, filteredEvents, searchHidEverything };
}
